import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  FileText,
  ShieldAlert,
  BookOpen,
  Newspaper,
  HelpCircle,
  ChevronDown,
  ArrowRight,
} from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { siteConfig } from "@/config/siteConfig";

const RESOURCE_CARDS = [
  {
    key: "tds",
    Icon: FileText,
    title: "Technical Data Sheets",
    body: "Product-specific technical data sheets available on request.",
    resource: "technical-data-sheet",
  },
  {
    key: "sds",
    Icon: ShieldAlert,
    title: "Safety Data Sheets",
    body: "Safety Data Sheets available on request.",
    resource: "safety-data-sheet",
  },
  {
    key: "guides",
    Icon: BookOpen,
    title: "Application Guides",
    body:
      "Application guides for pharmaceutical printing and coding scenarios available on request.",
    resource: "application-guide",
  },
  {
    key: "brochures",
    Icon: FileText,
    title: "Product Brochures",
    body: "Product brochures available on request.",
    resource: "product-brochure",
  },
  {
    key: "articles",
    Icon: Newspaper,
    title: "Technical Articles",
    body: "Read our latest technical articles.",
    linkTo: "/blog",
    linkLabel: "Visit the blog",
  },
];

const FAQS = [
  {
    q: `Does ${siteConfig.COMPANY_NAME} manufacture the products it supplies?`,
    a: `No. ${siteConfig.COMPANY_NAME} supplies specialised pharmaceutical printing, coding and production consumables from Dubai. Products are supplied subject to application testing under the customer's actual production conditions.`,
  },
  {
    q: "Which regions do you supply?",
    a: "We are Dubai-based and actively developing our international distribution network across the Middle East, Africa and Asia. Contact us to discuss supply into a specific market.",
  },
  {
    q: "Can I request a sample before placing a regular order?",
    a: "Yes. Selected products can be evaluated through a structured sample and trial process before regular supply. Use our sample request form and provide the machine, substrate and application details so we can assess suitability.",
  },
  {
    q: "Are your products certified for pharmaceutical use?",
    a: "Product suitability for a specific pharmaceutical manufacturing application must be established through appropriate testing under actual production conditions. We do not make regulatory approval claims on this website. Contact us to discuss the technical documentation available for the products relevant to your application.",
  },
  {
    q: "Do you work with distributors?",
    a: "Yes. We are actively developing distribution partnerships with experienced local partners serving pharmaceutical manufacturing, packaging, coding and industrial printing markets. Distribution arrangements, territories and any exclusivity are subject to commercial evaluation and agreed performance requirements. Apply via our Become a Distributor page.",
  },
];

const FaqItem = ({ faq, index, open, onToggle }) => (
  <div
    className="border border-[var(--border)] rounded-[4px] bg-white"
    data-testid={`resources-faq-${index}`}
  >
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="w-full text-left flex items-start justify-between gap-4 p-5"
    >
      <span className="font-heading text-[16.5px] font-semibold text-[var(--navy)]">
        {faq.q}
      </span>
      <ChevronDown
        className={`w-5 h-5 shrink-0 mt-0.5 transition-transform ${
          open ? "rotate-180" : ""
        }`}
        style={{ color: "var(--pharma-blue)" }}
        aria-hidden="true"
      />
    </button>
    {open && (
      <div className="px-5 pb-5 -mt-1 text-[14.5px] leading-relaxed text-[var(--charcoal)]">
        {faq.a}
      </div>
    )}
  </div>
);

const Resources = () => {
  const path = "/resources";
  const canonical = `${siteConfig.DOMAIN}${path}`;
  const [openFaq, setOpenFaq] = useState(0);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
      },
    })),
  };

  return (
    <>
      <SEO
        rawTitle={`Resources & FAQs | ${siteConfig.COMPANY_NAME}`}
        description="Technical data sheets, safety data sheets, application guides and product brochures available on request. FAQs on supply, samples and distribution."
        path={path}
        extraSchema={faqSchema}
      />
      <Helmet>
      </Helmet>

      <div className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10">
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Resources" }]} />
      </div>

      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10 pb-10"
        data-testid="resources-hero"
      >
        <p className="eyebrow">Technical Resources</p>
        <h1
          className="font-heading mt-4 text-[32px] leading-tight lg:text-[46px] lg:leading-[1.05] font-semibold text-[var(--navy)]"
          data-testid="resources-h1"
        >
          Product &amp; Application Resources
        </h1>
        <p className="mt-6 text-[16px] text-[var(--charcoal)] max-w-3xl">
          Technical documentation is available on request to qualified customers.
          Send us the details of your application and we will share the
          appropriate resources for the products relevant to your requirement.
        </p>
      </section>

      {/* Resource cards */}
      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 py-10"
        data-testid="resources-cards"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {RESOURCE_CARDS.map(({ key, Icon, title, body, resource, linkTo, linkLabel }) => (
            <article
              key={key}
              className="border border-[var(--border)] rounded-[4px] p-7 bg-white flex flex-col"
              data-testid={`resources-card-${key}`}
            >
              <Icon
                strokeWidth={1.5}
                style={{ width: 32, height: 32, color: "var(--pharma-blue)" }}
                aria-hidden="true"
              />
              <h2 className="font-heading mt-5 text-[19px] font-semibold text-[var(--navy)]">
                {title}
              </h2>
              <p className="mt-3 text-[14.5px] leading-relaxed text-[var(--slate)] flex-1">
                {body}
              </p>
              {linkTo ? (
                <Link to={linkTo} className="arrow-link mt-5" data-testid={`resources-card-${key}-link`}>
                  {linkLabel} <ArrowRight className="w-4 h-4" />
                </Link>
              ) : (
                <Link
                  to={`/contact?type=technical&resource=${resource}`}
                  className="arrow-link mt-5"
                  data-testid={`resources-card-${key}-link`}
                >
                  Document available on request <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </article>
          ))}

          {/* FAQ card — special: expands the FAQs list below */}
          <article
            className="border border-[var(--border)] rounded-[4px] p-7 bg-[var(--bg-alt)] flex flex-col"
            data-testid="resources-card-faqs"
          >
            <HelpCircle
              strokeWidth={1.5}
              style={{ width: 32, height: 32, color: "var(--pharma-blue)" }}
              aria-hidden="true"
            />
            <h2 className="font-heading mt-5 text-[19px] font-semibold text-[var(--navy)]">
              FAQs
            </h2>
            <p className="mt-3 text-[14.5px] leading-relaxed text-[var(--slate)] flex-1">
              Common questions on manufacturing, supply, samples, certification
              and distribution.
            </p>
            <a
              href="#faqs"
              className="arrow-link mt-5"
              data-testid="resources-card-faqs-link"
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById("faqs");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            >
              Read the FAQs <ArrowRight className="w-4 h-4" />
            </a>
          </article>
        </div>
      </section>

      {/* FAQ accordion */}
      <section
        id="faqs"
        className="mx-auto max-w-[1240px] px-6 lg:px-10 py-14 lg:py-20 scroll-mt-20 border-t border-[var(--border)] mt-6"
        data-testid="resources-faqs"
      >
        <p className="eyebrow">FAQs</p>
        <h2 className="font-heading mt-4 text-2xl lg:text-3xl font-semibold text-[var(--navy)]">
          Frequently asked questions
        </h2>
        <div className="mt-8 space-y-3 max-w-3xl">
          {FAQS.map((f, i) => (
            <FaqItem
              key={i}
              faq={f}
              index={i}
              open={openFaq === i}
              onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
            />
          ))}
        </div>
      </section>
    </>
  );
};

export default Resources;
